import { useState, useEffect, useRef, useMemo } from 'react';
import { apiGet, apiPost, apiDelete } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { FlightTrackerLoading } from '@/components/LoadingStates';
import { useWorkerPhoto, WorkerPhoto } from '@/hooks/useWorkerPhoto';
import Pagination from '@/components/Pagination';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, 
  Upload,
  Plane,
  Search,
  Loader2,
  Trash2,
  Link,
  Unlink,
  RefreshCw,
  Clock,
  MapPin,
  User,
  Calendar,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Globe,
  ArrowRight,
  Timer,
  Luggage
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

import FlightTrackingMap from '@/components/FlightTrackingMap';

// Helper function to format base64 image with proper data URI prefix
const formatBase64Image = (base64String) => {
  if (!base64String) return null;
  if (base64String.startsWith('data:image')) return base64String;
  return `data:image/jpeg;base64,${base64String}`;
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 100 } }
};

// Flight status configuration
const getFlightStatusConfig = (status) => {
  switch (status?.toLowerCase()) {
    case 'landed':
    case 'arrived':
      return {
        bg: 'bg-emerald-500',
        bgLight: 'bg-emerald-500/20',
        border: 'border-emerald-500/50',
        text: 'text-emerald-400',
        icon: CheckCircle,
        label: 'LANDED',
        color: '#22C55E',
        pulse: false
      };
    case 'in_flight':
    case 'active':
    case 'en-route':
      return {
        bg: 'bg-blue-500',
        bgLight: 'bg-blue-500/20',
        border: 'border-blue-500/50',
        text: 'text-blue-400',
        icon: Plane,
        label: 'IN FLIGHT',
        color: '#3B82F6',
        pulse: true
      };
    case 'delayed':
      return {
        bg: 'bg-amber-500',
        bgLight: 'bg-amber-500/20',
        border: 'border-amber-500/50',
        text: 'text-amber-400',
        icon: AlertTriangle,
        label: 'DELAYED',
        color: '#F59E0B',
        pulse: true
      };
    case 'cancelled':
      return {
        bg: 'bg-red-500',
        bgLight: 'bg-red-500/20',
        border: 'border-red-500/50',
        text: 'text-red-400',
        icon: XCircle,
        label: 'CANCELLED',
        color: '#EF4444',
        pulse: false
      };
    case 'scheduled':
    default:
      return {
        bg: 'bg-cyan-500',
        bgLight: 'bg-cyan-500/20',
        border: 'border-cyan-500/50',
        text: 'text-cyan-400',
        icon: Clock,
        label: 'SCHEDULED',
        color: '#22D3EE',
        pulse: false
      };
  }
};

// Realistic Airport Background Images
const airportImages = [
  'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80', // Airplane wing sunset
  'https://images.unsplash.com/photo-1529074963764-98f45c47344b?w=800&q=80', // Airport terminal
  'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=800&q=80', // Airplane on runway
  'https://images.unsplash.com/photo-1556388158-158ea5ccacbd?w=800&q=80', // Airplane takeoff
  'https://images.unsplash.com/photo-1559268950-2d7ceb2efa3a?w=800&q=80', // Night airport
];

// Single passenger row with lazy worker photo (supports multi-passenger tickets)
const PassengerRow = ({ ticketId, name, passport, linked, workersMap }) => {
  const workerFromMap = linked?.caseId && workersMap ? workersMap[linked.caseId] : null;
  const lazyPhoto = useWorkerPhoto(workerFromMap || (linked?.caseId ? { id: linked.caseId } : null));
  const photo = linked?.workerPhoto || linked?.photoData || workerFromMap?.photoData || workerFromMap?.workerPhoto || lazyPhoto || null;
  const workerName = linked?.workerName || workerFromMap?.workerName || null;
  const isLinked = !!linked;

  return (
    <div className="flex items-center gap-3" data-testid={`passenger-row-${ticketId}-${name}`}>
      {isLinked && photo ? (
        <div className="relative shrink-0">
          <img
            src={formatBase64Image(photo)}
            alt={workerName || name}
            className="w-12 h-12 rounded-xl object-cover border-2 border-emerald-500/50 shadow-lg shadow-emerald-500/20"
          />
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center border-2 border-slate-900">
            <CheckCircle className="h-3 w-3 text-white" />
          </div>
        </div>
      ) : isLinked ? (
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center border-2 border-emerald-500/50">
            <User className="h-6 w-6 text-emerald-400" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center border-2 border-slate-900">
            <CheckCircle className="h-3 w-3 text-white" />
          </div>
        </div>
      ) : (
        <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border-2 border-white/10 shrink-0">
          <User className="h-6 w-6 text-slate-500" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm truncate ${isLinked ? 'text-emerald-400' : 'text-white'}`}>
          {workerName || name}
        </p>
        {passport && (
          <p className="text-slate-500 text-xs font-mono truncate">{passport}</p>
        )}
        {isLinked && workerName && workerName !== name && (
          <p className="text-slate-600 text-xs truncate">Ticket: {name}</p>
        )}
      </div>
    </div>
  );
};

// Realistic Flight Ticket Card Component
const FlightTicketCard = ({ ticket, onRefresh, onDelete, onLink, refreshing, index, workersMap }) => {
  const statusConfig = getFlightStatusConfig(ticket.flightStatus?.status || ticket.status || 'scheduled');
  const StatusIcon = statusConfig.icon;
  
  // Extract flight details
  const flightNumber = ticket.flights?.[0]?.flightNumber || ticket.flightNumber || 'N/A';
  const airline = ticket.flights?.[0]?.airline || ticket.airline || 'Unknown Airline';
  
  // Build the full passenger list (supports multi-passenger tickets)
  const allPassengers = (ticket.passengers?.length
    ? ticket.passengers.map(p => ({ name: p.name, passport: p.passport_number || '' }))
    : (ticket.passengerNames?.length
      ? ticket.passengerNames.map(n => ({ name: n, passport: '' }))
      : [{ name: ticket.passengerName || 'Passenger', passport: '' }]));
  
  const linkedList = ticket.linkedPassengers || [];
  const normalizeName = (n) => (n || '').trim().toLowerCase();
  const linkedFor = (pName, idx) => {
    const byName = linkedList.find(lp => normalizeName(lp.passengerName) === normalizeName(pName));
    if (byName) return byName;
    // Legacy links without passengerName: try matching by worker name
    const byWorker = linkedList.find(lp => !lp.passengerName && normalizeName(lp.workerName).includes(normalizeName(pName)));
    if (byWorker) return byWorker;
    // Legacy single-passenger links may miss passengerName — apply to sole passenger
    if (allPassengers.length === 1 && linkedList.length > 0 && idx === 0) return linkedList[0];
    return null;
  };
  const linkedCount = allPassengers.filter((p, i) => linkedFor(p.name, i)).length;
  const isLinked = linkedCount > 0;
  
  // Departure info
  const departureAirport = ticket.flights?.[0]?.departureAirport || ticket.departureAirport || 'DEP';
  const departureCity = ticket.flights?.[0]?.departureCity || ticket.departureCity || departureAirport;
  const departureDate = ticket.flights?.[0]?.departureDate || ticket.departureDate || 'N/A';
  const departureTime = ticket.flights?.[0]?.departureTime || ticket.departureTime || '--:--';
  
  // Arrival info  
  const arrivalAirport = ticket.flights?.[0]?.arrivalAirport || ticket.arrivalAirport || 'ARR';
  const arrivalCity = ticket.flights?.[0]?.arrivalCity || ticket.arrivalCity || arrivalAirport;
  const arrivalDate = ticket.flights?.[0]?.arrivalDate || ticket.arrivalDate || 'N/A';
  const arrivalTime = ticket.flights?.[0]?.arrivalTime || ticket.arrivalTime || '--:--';
  
  // Booking reference
  const bookingRef = ticket.bookingReference || ticket.pnr || 'N/A';

  // Terminal / Gate (from extracted ticket OR live flight status — never hardcoded)
  const liveArrival = ticket.flightStatus?.arrival || ticket.flights?.[0]?.arrival || {};
  const liveDeparture = ticket.flightStatus?.departure || ticket.flights?.[0]?.departure || {};
  const arrivalTerminal =
    liveArrival.terminal ||
    ticket.arrivalTerminal ||
    ticket.flightArrivalTerminal ||
    '';
  const arrivalGate =
    liveArrival.gate ||
    ticket.arrivalGate ||
    ticket.flightArrivalGate ||
    '';
  const departureTerminal =
    liveDeparture.terminal ||
    ticket.departureTerminal ||
    '';
  const terminalGateLabel = (() => {
    const parts = [];
    if (arrivalTerminal) parts.push(`T${String(arrivalTerminal).replace(/^[Tt]/, '')}`);
    if (arrivalGate) parts.push(`Gate ${arrivalGate}`);
    if (parts.length) return parts.join(' / ');
    if (departureTerminal) return `Dep T${String(departureTerminal).replace(/^[Tt]/, '')}`;
    return '—';
  })();
  
  // Calculate flight duration (mock)
  const duration = ticket.flights?.[0]?.duration || '~7h 30m';
  
  // Get background image
  const bgImage = airportImages[index % airportImages.length];

  return (
    <motion.div variants={itemVariants} whileHover={{ y: -5 }}>
      <Card 
        className="relative overflow-hidden rounded-3xl border-0 shadow-2xl group"
        data-testid={`flight-ticket-${ticket.id}`}
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src={bgImage} 
            alt="Airport" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/90 to-slate-900/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
        </div>
        
        {/* Content */}
        <CardContent className="relative p-6 z-10">
          {/* Header Row - Airline & Status */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {/* Airline Logo Placeholder */}
              <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <Plane className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">{airline}</p>
                <p className="text-cyan-400 font-mono text-sm">{flightNumber}</p>
              </div>
            </div>
            
            {/* Status Badge */}
            <div className={`px-4 py-2 rounded-full ${statusConfig.bg} flex items-center gap-2 shadow-lg`}>
              <StatusIcon className={`h-4 w-4 text-white ${statusConfig.pulse ? 'animate-pulse' : ''}`} />
              <span className="text-white text-sm font-bold">{statusConfig.label}</span>
            </div>
          </div>
          
          {/* Flight Route - Main Display */}
          <div className="flex items-center justify-between mb-6">
            {/* Departure */}
            <div className="text-center">
              <p className="text-5xl font-black text-white mb-1">{departureAirport}</p>
              <p className="text-slate-400 text-sm">{departureCity}</p>
              <div className="mt-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/10">
                <p className="text-2xl font-bold text-cyan-400">{departureTime}</p>
                <p className="text-slate-500 text-xs">{departureDate}</p>
              </div>
            </div>
            
            {/* Flight Path Visualization */}
            <div className="flex-1 px-6 flex flex-col items-center">
              <div className="relative w-full flex items-center justify-center">
                {/* Dotted Line */}
                <div className="absolute w-full h-0.5 border-t-2 border-dashed border-white/30" />
                
                {/* Airplane Icon */}
                <div className={`relative z-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full p-3 shadow-lg shadow-cyan-500/50 ${statusConfig.pulse ? 'animate-bounce' : ''}`}>
                  <Plane className="h-5 w-5 text-white transform rotate-90" />
                </div>
                
                {/* Departure dot */}
                <div className="absolute left-0 w-3 h-3 rounded-full bg-cyan-400 border-2 border-white" />
                
                {/* Arrival dot */}
                <div className="absolute right-0 w-3 h-3 rounded-full bg-orange-400 border-2 border-white" />
              </div>
              
              {/* Duration */}
              <div className="mt-3 flex items-center gap-2 text-slate-400">
                <Timer className="h-4 w-4" />
                <span className="text-sm font-medium">{duration}</span>
              </div>
            </div>
            
            {/* Arrival */}
            <div className="text-center">
              <p className="text-5xl font-black text-white mb-1">{arrivalAirport}</p>
              <p className="text-slate-400 text-sm">{arrivalCity}</p>
              <div className="mt-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/10">
                <p className="text-2xl font-bold text-orange-400">{arrivalTime}</p>
                <p className="text-slate-500 text-xs">{arrivalDate}</p>
              </div>
            </div>
          </div>
          
          {/* Divider with Circles */}
          <div className="relative flex items-center my-4">
            <div className="absolute -left-6 w-6 h-6 rounded-full bg-slate-950" />
            <div className="flex-1 border-t-2 border-dashed border-white/20" />
            <div className="absolute -right-6 w-6 h-6 rounded-full bg-slate-950" />
          </div>
          
          {/* Passenger & Booking Info */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            {/* Passengers with Photos (all passengers on the ticket) */}
            <div className={`backdrop-blur-sm rounded-xl p-3 border ${isLinked ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/5 border-white/10'}`} data-testid={`ticket-passengers-${ticket.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <User className={`h-4 w-4 ${isLinked ? 'text-emerald-400' : 'text-slate-500'}`} />
                <span className={`text-xs uppercase ${isLinked ? 'text-emerald-400' : 'text-slate-500'}`}>
                  {allPassengers.length > 1
                    ? `${allPassengers.length} Passengers${linkedCount > 0 ? ` · ${linkedCount} ✓` : ''}`
                    : (isLinked ? '✓ Linked' : 'Passenger')}
                </span>
              </div>
              <div className="space-y-2.5">
                {allPassengers.map((p, i) => (
                  <PassengerRow
                    key={`${p.name}-${i}`}
                    ticketId={ticket.id}
                    name={p.name}
                    passport={p.passport}
                    linked={linkedFor(p.name, i)}
                    workersMap={workersMap}
                  />
                ))}
              </div>
            </div>
            
            {/* Booking Reference */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <Luggage className="h-4 w-4 text-slate-500" />
                <span className="text-slate-500 text-xs uppercase">Booking</span>
              </div>
              <p className="text-cyan-400 font-mono font-bold">{bookingRef}</p>
            </div>
            
            {/* Terminal / Gate (from real ticket or live flight status) */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="h-4 w-4 text-slate-500" />
                <span className="text-slate-500 text-xs uppercase">Terminal</span>
              </div>
              <p className="text-white font-semibold" data-testid={`ticket-terminal-${ticket.id}`}>
                {terminalGateLabel}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => onRefresh(ticket.id)}
              disabled={refreshing === ticket.id}
              className="flex-1 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/30 rounded-xl h-10"
            >
              {refreshing === ticket.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Status
                </>
              )}
            </Button>
            <Button
              size="sm"
              onClick={() => onLink(ticket)}
              className="flex-1 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30 rounded-xl h-10"
            >
              <Link className="h-4 w-4 mr-2" />
              Link Worker
            </Button>
            <Button
              size="sm"
              onClick={() => onDelete(ticket)}
              className="bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 rounded-xl h-10 w-10 p-0"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, color, trend }) => (
  <motion.div variants={itemVariants}>
    <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">{title}</p>
            <p className="text-3xl font-black text-white">{value}</p>
          </div>
          <div className="p-3 rounded-xl" style={{ background: `${color}20` }}>
            <Icon className="h-6 w-6" style={{ color }} />
          </div>
        </div>
        {trend && (
          <p className="text-xs mt-2" style={{ color }}>{trend}</p>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

// Main Component
const FlightScannerPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10; // Tickets per page
  
  // Link Dialog
  const [linkDialog, setLinkDialog] = useState({ open: false, ticket: null });
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedPassenger, setSelectedPassenger] = useState('');
  const [workerSearch, setWorkerSearch] = useState('');
  const [linking, setLinking] = useState(false);

  // Load tickets with photos for display
  const loadTickets = async () => {
    try {
      // Request photos for ticket cards display
      const data = await apiGet('/api/tickets?include_photos=true');
      setTickets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load workers for linking
  const loadWorkers = async () => {
    try {
      const data = await apiGet('/api/cases');
      setWorkers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading workers:', error);
    }
  };

  // Create workers map for quick lookup by ID (for getting photos)
  const workersMap = workers.reduce((map, worker) => {
    if (worker.id) {
      map[worker.id] = worker;
    }
    return map;
  }, {});

  useEffect(() => {
    loadTickets();
    loadWorkers();
  }, []);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setScanning(true);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const result = await apiPost('/api/tickets/upload', {
        ticketFileBase64: base64,
        ticketFileMimeType: file.type || 'image/jpeg'
      });

      if (result?.id) {
        // Check if extraction was successful
        const extractedError = result.extractedData?.error;
        if (extractedError) {
          toast.warning(extractedError || 'Could not extract some data - please verify details');
        } else {
          toast.success('Ticket scanned successfully!');
        }
        loadTickets();
      } else {
        throw new Error('Failed to scan ticket');
      }
    } catch (error) {
      console.error('Scan error:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to scan ticket';
      toast.error(`${errorMsg}. Please try with a clearer image.`);
    } finally {
      setScanning(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Refresh flight status (and auto-backfill terminal/gate from stored ticket image if missing)
  const handleRefreshStatus = async (ticketId) => {
    setRefreshing(ticketId);
    try {
      // 1) live flight status from Aviation Edge
      await apiPost(`/api/flights/refresh/${ticketId}`);

      // 2) if terminal still missing, ask backend to re-extract from stored image
      const ticket = tickets.find((t) => t.id === ticketId);
      const hasTerminal =
        ticket?.arrivalTerminal ||
        ticket?.flightArrivalTerminal ||
        ticket?.flightStatus?.arrival?.terminal;
      if (!hasTerminal) {
        try {
          const r = await apiPost(`/api/tickets/${ticketId}/rescan-fields`);
          if (r?.updated_fields && Object.keys(r.updated_fields).length > 0) {
            toast.success(`Backfilled: ${Object.keys(r.updated_fields).join(', ')}`);
          }
        } catch (e) {
          // Silent — re-scan is best-effort
          console.warn('Re-scan skipped:', e?.message);
        }
      }

      toast.success('Flight status updated');
      loadTickets();
    } catch (error) {
      toast.error('Failed to refresh status');
    } finally {
      setRefreshing(null);
    }
  };

  // Delete ticket
  const handleDelete = async (ticket) => {
    const passengerName = ticket.passengers?.[0]?.name || ticket.passengerNames?.[0] || 'this ticket';
    if (!window.confirm(`Delete ticket for ${passengerName}?`)) {
      return;
    }

    try {
      await apiDelete(`/api/tickets/${ticket.id}`);
      toast.success('Ticket deleted');
      loadTickets();
    } catch (error) {
      toast.error('Failed to delete ticket');
    }
  };

  // Link ticket to worker
  const handleLink = async () => {
    if (!selectedWorker || !linkDialog.ticket) return;

    const passengerNames = linkDialog.ticket.passengerNames || [];
    let passengerName = selectedPassenger;
    if (!passengerName && passengerNames.length === 1) {
      passengerName = passengerNames[0];
    }
    if (!passengerName && passengerNames.length > 1) {
      toast.error('Please select which passenger to link first');
      return;
    }

    setLinking(true);
    try {
      await apiPost('/api/tickets/link', {
        ticketId: linkDialog.ticket.id,
        caseId: selectedWorker.id,
        passengerName: passengerName || '',
      });
      toast.success(`Linked ${selectedWorker.workerName} → ${passengerName || 'passenger'}`);
      setLinkDialog({ open: false, ticket: null });
      setSelectedWorker(null);
      setSelectedPassenger('');
      loadTickets();
    } catch (error) {
      console.error('Link error:', error);
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.needs_passenger_selection) {
        toast.error('Multiple passengers on this ticket. Please pick one above.');
      } else if (typeof detail === 'string') {
        toast.error(`Failed to link ticket: ${detail}`);
      } else {
        toast.error(error?.message || 'Failed to link ticket');
      }
    } finally {
      setLinking(false);
    }
  };

  // Stats
  const stats = {
    total: tickets.length,
    scheduled: tickets.filter(t => !t.flightStatus?.status || t.flightStatus?.status?.toLowerCase() === 'scheduled').length,
    inFlight: tickets.filter(t => ['in_flight', 'active', 'en-route'].includes(t.flightStatus?.status?.toLowerCase())).length,
    landed: tickets.filter(t => ['landed', 'arrived'].includes(t.flightStatus?.status?.toLowerCase())).length,
    delayed: tickets.filter(t => t.flightStatus?.status?.toLowerCase() === 'delayed').length
  };

  // Filter tickets
  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      const matchesSearch = 
        (t.passengers?.[0]?.name || t.passengerNames?.[0] || t.passengerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.flights?.[0]?.flightNumber || t.flightNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.bookingReference || '').toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [tickets, searchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(filteredTickets.length / PAGE_SIZE);
  const paginatedTickets = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredTickets.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredTickets, currentPage, PAGE_SIZE]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="space-y-6" data-testid="flight-scanner-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="h-11 w-11 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/50"
          >
            <ArrowLeft className="h-5 w-5 text-white" />
          </Button>
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-white">Flight Tracker</h1>
            <p className="text-slate-500 text-sm">Track and manage flight tickets</p>
          </div>
        </div>
        
        {/* Upload Button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={scanning}
            className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-lg shadow-orange-500/25 rounded-xl px-6 h-12"
          >
            {scanning ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 mr-2" />
                Scan Ticket
              </>
            )}
          </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 md:grid-cols-5 gap-4"
      >
        <StatCard title="Total Tickets" value={stats.total} icon={Luggage} color="#22D3EE" />
        <StatCard title="Scheduled" value={stats.scheduled} icon={Clock} color="#22D3EE" trend="Upcoming flights" />
        <StatCard title="In Flight" value={stats.inFlight} icon={Plane} color="#3B82F6" trend="Currently airborne" />
        <StatCard title="Landed" value={stats.landed} icon={CheckCircle} color="#22C55E" trend="Arrived safely" />
        <StatCard title="Delayed" value={stats.delayed} icon={AlertTriangle} color="#F59E0B" trend="Check updates" />
      </motion.div>

      {/* Flight Tracking Map Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <FlightTrackingMap />
      </motion.div>

      {/* Search */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative"
      >
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by passenger name, flight number, or booking reference..."
          className="pl-12 h-12 bg-slate-900/70 backdrop-blur-xl border-white/10 text-white rounded-xl placeholder:text-slate-500"
        />
      </motion.div>

      {/* Tickets Grid */}
      {loading && tickets.length === 0 ? (
        <FlightTrackerLoading />
      ) : filteredTickets.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl">
            <CardContent className="p-16 text-center">
              <div className="h-24 w-24 mx-auto mb-6 rounded-2xl bg-slate-800/80 flex items-center justify-center">
                <Plane className="h-12 w-12 text-slate-600" />
              </div>
              <p className="text-slate-400 text-lg mb-2">
                {searchTerm ? 'No flights match your search' : 'No flight tickets yet'}
              </p>
              <p className="text-slate-600 text-sm mb-6">
                {searchTerm ? 'Try a different search term' : 'Upload a ticket to track flight status'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 rounded-xl px-6 h-11"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload First Ticket
                </Button>
              )}
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <>
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {paginatedTickets.map((ticket, index) => (
              <FlightTicketCard
                key={ticket.id}
                ticket={ticket}
                index={index}
                onRefresh={handleRefreshStatus}
                onDelete={handleDelete}
                onLink={(t) => setLinkDialog({ open: true, ticket: t })}
                refreshing={refreshing}
                workersMap={workersMap}
              />
            ))}
          </motion.div>
          
          {/* Pagination */}
          {filteredTickets.length > PAGE_SIZE && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={filteredTickets.length}
                pageSize={PAGE_SIZE}
                isLoading={loading}
              />
            </div>
          )}
        </>
      )}

      {/* Link Dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(open) => {
        setLinkDialog({ open, ticket: open ? linkDialog.ticket : null });
        if (!open) {
          setSelectedWorker(null);
          setSelectedPassenger('');
          setWorkerSearch('');
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Link className="h-5 w-5 text-orange-500" />
              Link Ticket to Worker
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* STEP 1 — Passenger picker (only if multiple passengers on ticket) */}
            {(() => {
              const pNames = linkDialog.ticket?.passengerNames || [];
              if (pNames.length <= 1) return null;
              return (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">
                    Step 1 · Pick passenger from ticket
                  </p>
                  <div className="space-y-1.5">
                    {pNames.map((name) => (
                      <div
                        key={name}
                        onClick={() => setSelectedPassenger(name)}
                        data-testid={`passenger-option-${name}`}
                        className={`p-3 rounded-xl cursor-pointer transition-all flex items-center gap-3 ${
                          selectedPassenger === name
                            ? 'bg-cyan-500/20 border border-cyan-500/50'
                            : 'bg-slate-800/50 border border-transparent hover:border-slate-700'
                        }`}
                      >
                        <User className="h-4 w-4 text-cyan-400" />
                        <span className="text-white font-mono text-sm flex-1">{name}</span>
                        {selectedPassenger === name && (
                          <CheckCircle className="h-4 w-4 text-cyan-500" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* STEP 2 — Worker search */}
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">
                {(linkDialog.ticket?.passengerNames || []).length > 1 ? 'Step 2 · ' : ''}Pick worker
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  value={workerSearch}
                  onChange={(e) => setWorkerSearch(e.target.value)}
                  placeholder="Search workers by name, passport, nationality..."
                  className="pl-10 bg-slate-800 border-slate-700 text-white rounded-xl"
                  data-testid="link-worker-search-input"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {workers
                .filter(w => {
                  const q = workerSearch.toLowerCase();
                  return (
                    (w.workerName || '').toLowerCase().includes(q) ||
                    (w.passportNumber || '').toLowerCase().includes(q) ||
                    (w.nationality || '').toLowerCase().includes(q)
                  );
                })
                .slice(0, 50)
                .map(worker => (
                  <div
                    key={worker.id}
                    onClick={() => setSelectedWorker(worker)}
                    data-testid={`link-worker-option-${worker.id}`}
                    className={`p-3 rounded-xl cursor-pointer transition-all flex items-center gap-3 ${
                      selectedWorker?.id === worker.id
                        ? 'bg-orange-500/20 border border-orange-500/50'
                        : 'bg-slate-800/50 border border-transparent hover:border-slate-700'
                    }`}
                  >
                    {/* Worker Photo */}
                    <div className="w-12 h-12 rounded-xl bg-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0 border border-slate-600">
                      <WorkerPhoto
                        worker={worker}
                        alt={worker.workerName}
                        className="w-full h-full object-cover"
                        fallback={<User className="h-6 w-6 text-slate-500" />}
                      />
                    </div>
                    {/* Worker Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{worker.workerName}</p>
                      <p className="text-slate-500 text-sm truncate">{worker.nationality} • {worker.passportNumber || 'No passport'}</p>
                      <p className="text-xs text-slate-600">{worker.status === 'abroad' ? '🌍 بالخارج' : worker.status === 'arrived' ? '✅ وصلت' : worker.status}</p>
                    </div>
                    {/* Selection indicator */}
                    {selectedWorker?.id === worker.id && (
                      <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    )}
                  </div>
                ))}
              {workers.length > 0 && workerSearch && workers.filter(w => {
                const q = workerSearch.toLowerCase();
                return (w.workerName || '').toLowerCase().includes(q) ||
                       (w.passportNumber || '').toLowerCase().includes(q) ||
                       (w.nationality || '').toLowerCase().includes(q);
              }).length === 0 && (
                <p className="text-center text-slate-500 text-sm py-4">
                  No workers match "{workerSearch}"
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkDialog({ open: false, ticket: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleLink}
              disabled={
                !selectedWorker ||
                linking ||
                ((linkDialog.ticket?.passengerNames || []).length > 1 && !selectedPassenger)
              }
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="link-ticket-confirm-btn"
            >
              {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Link Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlightScannerPage;
